import type { ExercisePayload, Item, MatchingPair, Skill } from "./types";

export const studySkills: Array<[Skill, string]> = [
  ["recognition", "Flashcard"],
  ["dictation", "Nghe - viết"],
  ["production", "Dùng từ"],
  ["meaning_choice", "Chọn nghĩa đúng"],
  ["word_choice", "Chọn từ đúng"],
  ["matching", "Nối từ với nghĩa"],
  ["audio_word_choice", "Nghe và chọn từ"],
  ["audio_meaning_choice", "Nghe và chọn nghĩa"],
  ["spelling_correction", "Sửa lỗi chính tả"],
  ["missing_letters", "Điền chữ còn thiếu"],
];

export const skillLabels = Object.fromEntries(studySkills) as Record<Skill, string>;
export const studySkillValues = studySkills.map(([skill]) => skill);

type ExerciseItem = Pick<Item, "id" | "term" | "meaning_vi" | "part_of_speech">;

function hash(value: string) {
  return [...value].reduce((total, character) => (total * 31 + character.charCodeAt(0)) >>> 0, 7);
}

function shuffle<T>(values: T[], seed: number) {
  const output = [...values];
  let state = seed || 1;
  for (let index = output.length - 1; index > 0; index -= 1) {
    state = (state * 1664525 + 1013904223) >>> 0;
    const swapIndex = state % (index + 1);
    [output[index], output[swapIndex]] = [output[swapIndex], output[index]];
  }
  return output;
}

function distractors(items: ExerciseItem[], target: ExerciseItem, field: "term" | "meaning_vi") {
  const samePart = items.filter((item) => item.id !== target.id && item.part_of_speech === target.part_of_speech && item[field]);
  const all = items.filter((item) => item.id !== target.id && item[field]);
  return shuffle([...samePart, ...all.filter((item) => !samePart.some((candidate) => candidate.id === item.id))], hash(`${target.id}:${field}`)).slice(0, 3);
}

function uniqueValues(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

function corruptedTerm(term: string) {
  const letters = [...term];
  const indexes = letters.map((character, index) => /[A-Za-z]/.test(character) ? index : -1).filter((index) => index >= 0);
  if (!indexes.length) return `${term}?`;
  const index = indexes[hash(term) % indexes.length];
  const nextIndex = indexes.find((candidate) => candidate > index);
  if (nextIndex !== undefined && letters[index].toLocaleLowerCase() !== letters[nextIndex].toLocaleLowerCase()) {
    [letters[index], letters[nextIndex]] = [letters[nextIndex], letters[index]];
  } else if (letters.length > 2) {
    letters.splice(index, 1);
  } else {
    letters.splice(index, 0, letters[index]);
  }
  return letters.join("");
}

function maskedTerm(term: string) {
  const letters = [...term];
  const indexes = letters.map((character, index) => /[A-Za-z]/.test(character) ? index : -1).filter((index) => index >= 0);
  const count = Math.max(1, Math.min(3, Math.floor(indexes.length / 3)));
  const hidden = new Set(shuffle(indexes, hash(`${term}:mask`)).slice(0, count));
  return letters.map((character, index) => hidden.has(index) ? "_" : character).join("");
}

function matchingPairs(items: ExerciseItem[], target: ExerciseItem): MatchingPair[] {
  const related = items.filter((item) => item.meaning_vi && item.term);
  const pool = shuffle(related.filter((item) => item.id !== target.id), hash(`${target.id}:matching`)).slice(0, 7);
  const pairs = [target, ...pool].filter((item, index, values) => values.findIndex((candidate) => candidate.term === item.term || candidate.meaning_vi === item.meaning_vi) === index).slice(0, 8);
  return shuffle(pairs, hash(`${target.id}:matching-order`)).map((item) => ({ term: item.term, meaning: item.meaning_vi }));
}

export function buildExercise(skill: Skill, target: ExerciseItem, items: ExerciseItem[]): ExercisePayload | null {
  switch (skill) {
    case "meaning_choice":
      return { prompt: "Chọn nghĩa tiếng Việt phù hợp với từ này.", options: shuffle(uniqueValues([target, ...distractors(items, target, "meaning_vi")].map((item) => item.meaning_vi)), hash(`${target.id}:meaning-choice`)) };
    case "word_choice":
      return { prompt: "Chọn từ tiếng Anh phù hợp với nghĩa này.", options: shuffle(uniqueValues([target, ...distractors(items, target, "term")].map((item) => item.term)), hash(`${target.id}:word-choice`)) };
    case "matching":
      return { prompt: "Ghép mỗi từ tiếng Anh với nghĩa tiếng Việt tương ứng.", matching_pairs: matchingPairs(items, target) };
    case "audio_word_choice":
      return { prompt: "Nghe và chọn từ bạn nghe được.", options: shuffle(uniqueValues([target, ...distractors(items, target, "term")].map((item) => item.term)), hash(`${target.id}:audio-word-choice`)) };
    case "audio_meaning_choice":
      return { prompt: "Nghe và chọn nghĩa phù hợp.", options: shuffle(uniqueValues([target, ...distractors(items, target, "meaning_vi")].map((item) => item.meaning_vi)), hash(`${target.id}:audio-meaning-choice`)) };
    case "spelling_correction":
      return { prompt: "Sửa từ bị viết sai thành từ đúng.", corrupted_term: corruptedTerm(target.term) };
    case "missing_letters":
      return { prompt: "Điền chữ còn thiếu rồi gõ toàn bộ từ.", masked_term: maskedTerm(target.term) };
    default:
      return null;
  }
}
